import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import MasterDataSelect from "@/components/MasterDataSelect";
import MasterDataMultiSelect from "@/components/MasterDataMultiSelect";
import { useMasterList } from "@/hooks/useMasterData";
import { formatINR, formatNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const emptyForm = {
  client_type: "individual",
  contact_persons: [{ name: "", designation: "", contact_number: "" }],
  nature_of_business_id: "",
  nature_of_business_other: "",
  office_address: "",
  brief_note: "",
  lead_source_id: "",
  lead_source_detail: "",
  lead_name: "",
  contact_no: "",
  alternate_no: "",
  email: "",
  company_name: "",
  asset_category_id: "",
  asset_type_id: "",
  area_min: "", area_max: "", area_unit_id: "",
  budget_min: "", budget_max: "", budget_unit_id: "",
  preferred_city_ids: [],
  preferred_village_ids: [],
  short_description: "",
  buyer_type_id: "", risk_appetite: "", investment_horizon: "", urgency: "",
  min_discount_required: "", brokerage_type: "", brokerage_rate: "", brokerage_payable_by: "",
  lead_quality_id: "", lead_status_id: "", lead_warmth_id: "",
  next_contact_date: "", group_ids: [], tag_ids: [],
  lead_closed: false, closure_reason_id: "",
  lead_owner_id: "",
};

function FL({ children, required }) {
  return (
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
      {children}
      {required && <span className="text-[#B8935A] ml-0.5">*</span>}
    </Label>
  );
}

export default function LeadForm({ client = false }) {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.roles || [user?.role]).includes("admin");
  const [form, setForm] = useState({ ...emptyForm, client_type: client ? "institutional" : "individual" });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const { data: areaUnits = [] } = useMasterList("area_unit");
  const { data: priceUnits = [] } = useMasterList("price_unit");
  const { data: nobList = [] } = useMasterList("nature_of_business");
  const isOtherNob = useMemo(
    () => nobList.find((n) => n.id === form.nature_of_business_id)?.label === "Other",
    [nobList, form.nature_of_business_id]
  );
  const { data: groups = [] } = useQuery({
    queryKey: ["lead-groups"],
    queryFn: async () => (await api.get("/lead-groups")).data,
  });

  const { data: usersList = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/auth/users")).data,
    enabled: isAdmin,
  });

  const { data: existing } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => (await api.get(`/leads/${id}`)).data,
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing) {
      setForm({
        ...emptyForm,
        ...existing,
        lead_source_detail: existing.lead_source_detail || "",
        alternate_no: existing.alternate_no || "",
        email: existing.email || "",
        company_name: existing.company_name || "",
        short_description: existing.short_description || "",
        buyer_type_id: existing.buyer_type_id || "",
        risk_appetite: existing.risk_appetite || "",
        investment_horizon: existing.investment_horizon || "",
        urgency: existing.urgency || "",
        min_discount_required: existing.min_discount_required ?? "",
        brokerage_type: existing.brokerage_type || "",
        brokerage_rate: existing.brokerage_rate ?? "",
        brokerage_payable_by: existing.brokerage_payable_by || "",
        lead_quality_id: existing.lead_quality_id || "",
        lead_status_id: existing.lead_status_id || "",
        lead_warmth_id: existing.lead_warmth_id || "",
        next_contact_date: existing.next_contact_date || "",
        closure_reason_id: existing.closure_reason_id || "",
        lead_owner_id: existing.lead_owner_id || "",
        group_ids: existing.group_ids || [],
        tag_ids: existing.tag_ids || [],
        client_type: existing.client_type || "individual",
        contact_persons: (existing.contact_persons && existing.contact_persons.length)
          ? existing.contact_persons.map((p) => ({ name: p.name || "", designation: p.designation || "", contact_number: p.contact_number || "" }))
          : [{ name: "", designation: "", contact_number: "" }],
        nature_of_business_id: existing.nature_of_business_id || "",
        nature_of_business_other: existing.nature_of_business_other || "",
        office_address: existing.office_address || "",
        brief_note: existing.brief_note || "",
      });
    }
  }, [existing]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  // Contact persons (institutional clients — max 4)
  const setCP = (idx, key, val) => setForm((f) => {
    const cps = f.contact_persons.map((p, i) => (i === idx ? { ...p, [key]: val } : p));
    return { ...f, contact_persons: cps };
  });
  const addCP = () => setForm((f) => (
    f.contact_persons.length >= 4 ? f : { ...f, contact_persons: [...f.contact_persons, { name: "", designation: "", contact_number: "" }] }
  ));
  const removeCP = (idx) => setForm((f) => ({
    ...f,
    contact_persons: f.contact_persons.length <= 1 ? f.contact_persons : f.contact_persons.filter((_, i) => i !== idx),
  }));

  // Computed
  const areaCF = useMemo(() => areaUnits.find((u) => u.id === form.area_unit_id)?.conversion_factor || 0, [areaUnits, form.area_unit_id]);
  const priceCF = useMemo(() => priceUnits.find((u) => u.id === form.budget_unit_id)?.conversion_factor || 0, [priceUnits, form.budget_unit_id]);
  const areaMinSqft = Number(form.area_min || 0) * areaCF;
  const areaMaxSqft = Number(form.area_max || 0) * areaCF;
  const budgetMinINR = Number(form.budget_min || 0) * priceCF;
  const budgetMaxINR = Number(form.budget_max || 0) * priceCF;

  function validate() {
    const e = {};
    const req = client
      ? ["lead_source_id", "lead_name", "asset_category_id", "asset_type_id",
         "area_min", "area_max", "area_unit_id", "budget_min", "budget_max", "budget_unit_id"]
      : ["lead_source_id", "lead_name", "contact_no",
         "asset_category_id", "asset_type_id",
         "area_min", "area_max", "area_unit_id",
         "budget_min", "budget_max", "budget_unit_id"];
    for (const f of req) if (!form[f] && form[f] !== 0) e[f] = "Required";
    if ((form.preferred_city_ids || []).length === 0) e.preferred_city_ids = "At least one city";
    if ((form.preferred_village_ids || []).length === 0) e.preferred_village_ids = "At least one village/town";
    if (form.contact_no && !/^\+?[0-9 .\-()]{7,20}$/.test(form.contact_no))
      e.contact_no = "Invalid phone number";
    if (client) {
      const firstCpNo = (form.contact_persons?.[0]?.contact_number || "").trim();
      if (!form.contact_no && !firstCpNo) e.contact_no = "Provide a main office number or a contact person number";
      if (form.nature_of_business_id === "__OTHER__" || isOtherNob) {
        if (!form.nature_of_business_other?.trim()) e.nature_of_business_other = "Please specify";
      }
    }
    if (form.area_min && form.area_max && Number(form.area_max) < Number(form.area_min))
      e.area_max = "Max must be >= Min";
    if (form.budget_min && form.budget_max && Number(form.budget_max) < Number(form.budget_min))
      e.budget_max = "Max must be >= Min";
    if (form.lead_closed && !form.closure_reason_id)
      e.closure_reason_id = "Required when lead is closed";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted errors");
      return;
    }
    const payload = { ...form };
    payload.area_min = Number(payload.area_min);
    payload.area_max = Number(payload.area_max);
    payload.budget_min = Number(payload.budget_min);
    payload.budget_max = Number(payload.budget_max);
    if (payload.min_discount_required === "" || payload.min_discount_required == null) payload.min_discount_required = null;
    else payload.min_discount_required = Number(payload.min_discount_required);
    if (payload.brokerage_rate === "" || payload.brokerage_rate == null) payload.brokerage_rate = null;
    else payload.brokerage_rate = Number(payload.brokerage_rate);
    for (const k of [
      "lead_source_detail", "alternate_no", "email", "company_name", "short_description",
      "buyer_type_id", "risk_appetite", "investment_horizon", "urgency",
      "brokerage_type", "brokerage_payable_by",
      "lead_quality_id", "lead_status_id", "lead_warmth_id",
      "next_contact_date", "closure_reason_id", "lead_owner_id",
      "nature_of_business_id", "nature_of_business_other", "office_address", "brief_note",
    ]) {
      if (payload[k] === "") payload[k] = null;
    }
    if (client) {
      payload.client_type = "institutional";
      // keep only filled contact-person rows
      payload.contact_persons = (form.contact_persons || [])
        .map((p) => ({ name: (p.name || "").trim(), designation: (p.designation || "").trim(), contact_number: (p.contact_number || "").trim() }))
        .filter((p) => p.name || p.designation || p.contact_number);
      if (!payload.contact_no) payload.contact_no = payload.contact_persons[0]?.contact_number || "";
      if (!isOtherNob) payload.nature_of_business_other = null;
    } else {
      payload.client_type = "individual";
      payload.contact_persons = [];
    }
    setSubmitting(true);
    try {
      const res = isEdit
        ? await api.put(`/leads/${id}`, payload)
        : await api.post("/leads", payload);
      toast.success(isEdit ? (client ? "Client updated" : "Lead updated") : (client ? "Client created" : "Lead created"));
      navigate(`${client ? "/clients" : "/leads"}/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="pb-32" data-testid="lead-form">
      <div className="px-8 pt-8 max-w-5xl">
        <button type="button" onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-button">
          <ChevronLeft className="h-3 w-3" /> Back
        </button>
        <div className="mt-2">
          <div className="label-eyebrow">{isEdit ? (client ? "Edit Client" : "Edit Lead") : "Create New"}</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">
            {isEdit ? form.lead_name || (client ? "Client" : "Lead") : (client ? "Add Client" : "Add Lead")}
          </h1>
          {isEdit && existing?.lead_code && <div className="font-mono-num text-xs text-slate-500 mt-1">{existing.lead_code}</div>}
        </div>
      </div>

      <div className="px-8 mt-6 max-w-5xl">
        <Accordion type="multiple" defaultValue={["s1", "s2", "s3"]} className="space-y-3">
          {/* Section 1 */}
          <AccordionItem value="s1" className="bg-white border border-slate-200 rounded-md" data-testid="lead-section-1">
            <AccordionTrigger className="px-5 hover:no-underline">
              <div className="text-left">
                <div className="label-eyebrow">Section 1</div>
                <div className="font-display text-lg font-semibold text-slate-900">{client ? "Client Identification" : "Lead Identification"}</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              {client ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <FL required>Lead Source</FL>
                    <div className="mt-1">
                      <MasterDataSelect listType="lead_source" value={form.lead_source_id} onChange={(v) => set("lead_source_id", v)} testId="field-lead-source" />
                    </div>
                    {errors.lead_source_id && <p className="text-xs text-red-700 mt-1">{errors.lead_source_id}</p>}
                  </div>
                  <div>
                    <FL required>Client Name</FL>
                    <Input value={form.lead_name} onChange={(e) => set("lead_name", e.target.value)} placeholder="Institution / organisation name" className="mt-1" data-testid="field-lead-name" />
                    {errors.lead_name && <p className="text-xs text-red-700 mt-1">{errors.lead_name}</p>}
                  </div>
                  <div>
                    <FL>Nature of Business</FL>
                    <div className="mt-1">
                      <MasterDataSelect listType="nature_of_business" value={form.nature_of_business_id} onChange={(v) => set("nature_of_business_id", v)} testId="field-nature-of-business" />
                    </div>
                  </div>
                  {isOtherNob && (
                    <div>
                      <FL required>Please specify</FL>
                      <Input value={form.nature_of_business_other} onChange={(e) => set("nature_of_business_other", e.target.value)} placeholder="Specify nature of business" className="mt-1" data-testid="field-nature-of-business-other" />
                      {errors.nature_of_business_other && <p className="text-xs text-red-700 mt-1">{errors.nature_of_business_other}</p>}
                    </div>
                  )}
                  <div>
                    <FL>Email ID</FL>
                    <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@example.com" className="mt-1" data-testid="field-email" />
                  </div>
                  <div>
                    <FL>Main Office Contact No.</FL>
                    <Input value={form.contact_no} onChange={(e) => set("contact_no", e.target.value)} placeholder="+91 22 1234 5678 (switchboard)" className="mt-1 font-mono-num" data-testid="field-contact-no" />
                    {errors.contact_no && <p className="text-xs text-red-700 mt-1">{errors.contact_no}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <FL>Office Address</FL>
                    <Textarea value={form.office_address} onChange={(e) => set("office_address", e.target.value)} placeholder="Registered / office address" className="mt-1" data-testid="field-office-address" />
                  </div>
                  <div className="md:col-span-2">
                    <FL>Brief Note</FL>
                    <Textarea value={form.brief_note} onChange={(e) => set("brief_note", e.target.value)} placeholder="General context about this client" className="mt-1" data-testid="field-brief-note" />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between">
                      <FL>Contact Persons (max 4)</FL>
                      <Button type="button" size="sm" variant="outline" onClick={addCP} disabled={form.contact_persons.length >= 4} data-testid="add-contact-person">
                        + Add Contact Person
                      </Button>
                    </div>
                    <div className="mt-2 space-y-2">
                      {form.contact_persons.map((p, i) => (
                        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-start" data-testid={`contact-person-row-${i}`}>
                          <Input value={p.name} onChange={(e) => setCP(i, "name", e.target.value)} placeholder="Name" data-testid={`cp-name-${i}`} />
                          <Input value={p.designation} onChange={(e) => setCP(i, "designation", e.target.value)} placeholder="Designation (e.g. CFO)" data-testid={`cp-designation-${i}`} />
                          <Input value={p.contact_number} onChange={(e) => setCP(i, "contact_number", e.target.value)} placeholder="Contact number" className="font-mono-num" data-testid={`cp-number-${i}`} />
                          <Button type="button" size="icon" variant="ghost" onClick={() => removeCP(i)} disabled={form.contact_persons.length <= 1} className="text-slate-400 hover:text-red-600" data-testid={`cp-remove-${i}`} aria-label="Remove contact person">
                            ×
                          </Button>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1.5">The first contact person's number is used as the primary Call/WhatsApp number if the main office number is blank.</p>
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <FL required>Lead Source</FL>
                  <div className="mt-1">
                    <MasterDataSelect listType="lead_source" value={form.lead_source_id} onChange={(v) => set("lead_source_id", v)} testId="field-lead-source" />
                  </div>
                  {errors.lead_source_id && <p className="text-xs text-red-700 mt-1">{errors.lead_source_id}</p>}
                </div>
                <div>
                  <FL>Lead Source Detail</FL>
                  <Input value={form.lead_source_detail} onChange={(e) => set("lead_source_detail", e.target.value)} placeholder="Campaign / form name" className="mt-1" data-testid="field-lead-source-detail" />
                </div>
                <div>
                  <FL required>Lead Name</FL>
                  <Input value={form.lead_name} onChange={(e) => set("lead_name", e.target.value)} placeholder="Contact person name" className="mt-1" data-testid="field-lead-name" />
                  {errors.lead_name && <p className="text-xs text-red-700 mt-1">{errors.lead_name}</p>}
                </div>
                <div>
                  <FL>Company Name</FL>
                  <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Company / Family Office" className="mt-1" data-testid="field-company-name" />
                </div>
                <div>
                  <FL required>Contact No.</FL>
                  <Input value={form.contact_no} onChange={(e) => set("contact_no", e.target.value)} placeholder="+91 98765 43210" className="mt-1 font-mono-num" data-testid="field-contact-no" />
                  {errors.contact_no && <p className="text-xs text-red-700 mt-1">{errors.contact_no}</p>}
                </div>
                <div>
                  <FL>Alternate No.</FL>
                  <Input value={form.alternate_no} onChange={(e) => set("alternate_no", e.target.value)} placeholder="+91 …" className="mt-1 font-mono-num" data-testid="field-alternate-no" />
                </div>
                <div className="md:col-span-2">
                  <FL>Email ID</FL>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@example.com" className="mt-1" data-testid="field-email" />
                </div>
              </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Section 2 — Requirement */}
          <AccordionItem value="s2" className="bg-white border border-slate-200 rounded-md" data-testid="lead-section-2">
            <AccordionTrigger className="px-5 hover:no-underline">
              <div className="text-left">
                <div className="label-eyebrow">Section 2</div>
                <div className="font-display text-lg font-semibold text-slate-900">Client Requirement</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <FL required>Asset Category</FL>
                  <div className="mt-1">
                    <MasterDataSelect
                      listType="asset_category"
                      value={form.asset_category_id}
                      onChange={(v) => { set("asset_category_id", v); set("asset_type_id", ""); }}
                      testId="field-asset-category"
                    />
                  </div>
                  {errors.asset_category_id && <p className="text-xs text-red-700 mt-1">{errors.asset_category_id}</p>}
                </div>
                <div>
                  <FL required>Asset Type</FL>
                  <div className="mt-1">
                    <MasterDataSelect
                      listType="asset_type"
                      parentId={form.asset_category_id || null}
                      value={form.asset_type_id}
                      onChange={(v) => set("asset_type_id", v)}
                      disabled={!form.asset_category_id}
                      placeholder={form.asset_category_id ? "Select type…" : "Pick a category first"}
                      testId="field-asset-type"
                    />
                  </div>
                  {errors.asset_type_id && <p className="text-xs text-red-700 mt-1">{errors.asset_type_id}</p>}
                </div>
                <div>
                  <FL required>Area Range</FL>
                  <div className="mt-1 flex gap-2">
                    <Input type="number" min="0" value={form.area_min} onChange={(e) => set("area_min", e.target.value)} placeholder="Min" className="flex-1" data-testid="field-area-min" />
                    <Input type="number" min="0" value={form.area_max} onChange={(e) => set("area_max", e.target.value)} placeholder="Max" className="flex-1" data-testid="field-area-max" />
                    <div className="w-32">
                      <MasterDataSelect listType="area_unit" value={form.area_unit_id} onChange={(v) => set("area_unit_id", v)} placeholder="Unit" testId="field-area-unit" />
                    </div>
                  </div>
                  {areaCF > 0 && (form.area_min || form.area_max) && (
                    <p className="text-[11px] font-mono-num text-slate-500 mt-1">
                      = {formatNumber(Math.round(areaMinSqft))} – {formatNumber(Math.round(areaMaxSqft))} sq.ft
                    </p>
                  )}
                  {(errors.area_min || errors.area_max || errors.area_unit_id) && <p className="text-xs text-red-700 mt-1">{errors.area_min || errors.area_max || errors.area_unit_id}</p>}
                </div>
                <div>
                  <FL required>Budget Range</FL>
                  <div className="mt-1 flex gap-2">
                    <Input type="number" min="0" step="0.01" value={form.budget_min} onChange={(e) => set("budget_min", e.target.value)} placeholder="Min" className="flex-1" data-testid="field-budget-min" />
                    <Input type="number" min="0" step="0.01" value={form.budget_max} onChange={(e) => set("budget_max", e.target.value)} placeholder="Max" className="flex-1" data-testid="field-budget-max" />
                    <div className="w-28">
                      <MasterDataSelect listType="price_unit" value={form.budget_unit_id} onChange={(v) => set("budget_unit_id", v)} placeholder="Unit" testId="field-budget-unit" />
                    </div>
                  </div>
                  {priceCF > 0 && (form.budget_min || form.budget_max) && (
                    <p className="text-[11px] font-mono-num text-slate-500 mt-1">
                      = {formatINR(budgetMinINR)} – {formatINR(budgetMaxINR)}
                    </p>
                  )}
                  {(errors.budget_min || errors.budget_max || errors.budget_unit_id) && <p className="text-xs text-red-700 mt-1">{errors.budget_min || errors.budget_max || errors.budget_unit_id}</p>}
                </div>
                <div>
                  <FL required>Preferred Cities</FL>
                  <div className="mt-1">
                    <MasterDataMultiSelect
                      listType="city"
                      values={form.preferred_city_ids}
                      onChange={(v) => {
                        // When cities change, prune villages whose parent city is no longer selected.
                        set("preferred_city_ids", v);
                      }}
                      testId="field-preferred-cities"
                    />
                  </div>
                  {errors.preferred_city_ids && <p className="text-xs text-red-700 mt-1">{errors.preferred_city_ids}</p>}
                </div>
                <div>
                  <FL required>Preferred Village/Towns</FL>
                  <div className="mt-1">
                    <MasterDataMultiSelect
                      listType="village_town"
                      parentIds={form.preferred_city_ids || []}
                      values={form.preferred_village_ids}
                      onChange={(v) => set("preferred_village_ids", v)}
                      disabled={(form.preferred_city_ids || []).length === 0}
                      placeholder={(form.preferred_city_ids || []).length === 0 ? "Pick cities first" : "Pick villages/towns"}
                      testId="field-preferred-villages"
                    />
                  </div>
                  {errors.preferred_village_ids && <p className="text-xs text-red-700 mt-1">{errors.preferred_village_ids}</p>}
                </div>
                <div className="md:col-span-2">
                  <FL>Short Description</FL>
                  <Textarea value={form.short_description} onChange={(e) => set("short_description", e.target.value)} rows={2} placeholder="What's the client looking for…" className="mt-1" data-testid="field-short-description" />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Section 2B — Qualification (hidden for institutional clients; brokerage handled at Deal level) */}
          {!client && (
          <AccordionItem value="s2b" className="bg-white border border-slate-200 rounded-md" data-testid="lead-section-2b">
            <AccordionTrigger className="px-5 hover:no-underline">
              <div className="text-left">
                <div className="label-eyebrow">Section 2B</div>
                <div className="font-display text-lg font-semibold text-slate-900">Qualification (progressive)</div>
                <div className="text-xs text-slate-500 mt-0.5">Fill in after first contact.</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <FL>Buyer Type</FL>
                  <div className="mt-1"><MasterDataSelect listType="buyer_type" value={form.buyer_type_id} onChange={(v) => set("buyer_type_id", v)} testId="field-buyer-type" /></div>
                </div>
                <div>
                  <FL>Risk Appetite</FL>
                  <Select value={form.risk_appetite || ""} onValueChange={(v) => set("risk_appetite", v)}>
                    <SelectTrigger className="mt-1" data-testid="field-risk-appetite"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FL>Investment Horizon</FL>
                  <Select value={form.investment_horizon || ""} onValueChange={(v) => set("investment_horizon", v)}>
                    <SelectTrigger className="mt-1" data-testid="field-investment-horizon"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="<6 months">&lt;6 months</SelectItem>
                      <SelectItem value="6-18 months">6–18 months</SelectItem>
                      <SelectItem value=">18 months">&gt;18 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FL>Urgency</FL>
                  <Select value={form.urgency || ""} onValueChange={(v) => set("urgency", v)}>
                    <SelectTrigger className="mt-1" data-testid="field-urgency"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Immediate">Immediate</SelectItem>
                      <SelectItem value="1-3 months">1–3 months</SelectItem>
                      <SelectItem value="Exploratory">Exploratory</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FL>Min Discount Required (%)</FL>
                  <Input type="number" min="0" step="0.1" value={form.min_discount_required} onChange={(e) => set("min_discount_required", e.target.value)} className="mt-1 font-mono-num" data-testid="field-min-discount" />
                </div>
                <div>
                  <FL>Brokerage Type</FL>
                  <Select value={form.brokerage_type || ""} onValueChange={(v) => set("brokerage_type", v)}>
                    <SelectTrigger className="mt-1" data-testid="field-brokerage-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="% of deal value">% of deal value</SelectItem>
                      <SelectItem value="Fixed fee">Fixed fee</SelectItem>
                      <SelectItem value="Retainer + Success fee">Retainer + Success fee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FL>Brokerage Rate</FL>
                  <Input type="number" min="0" step="0.01" value={form.brokerage_rate} onChange={(e) => set("brokerage_rate", e.target.value)} className="mt-1 font-mono-num" data-testid="field-brokerage-rate" />
                </div>
                <div>
                  <FL>Brokerage Payable By</FL>
                  <Select value={form.brokerage_payable_by || ""} onValueChange={(v) => set("brokerage_payable_by", v)}>
                    <SelectTrigger className="mt-1" data-testid="field-brokerage-payable-by"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Buyer">Buyer</SelectItem>
                      <SelectItem value="Seller-ARC-Bank">Seller-ARC-Bank</SelectItem>
                      <SelectItem value="Both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
          )}

          {/* Section 3 — Lead Management */}
          <AccordionItem value="s3" className="bg-white border border-slate-200 rounded-md" data-testid="lead-section-3">
            <AccordionTrigger className="px-5 hover:no-underline">
              <div className="text-left">
                <div className="label-eyebrow">Section 3</div>
                <div className="font-display text-lg font-semibold text-slate-900">Lead Management</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <FL>Lead Quality</FL>
                  <div className="mt-1"><MasterDataSelect listType="lead_quality" value={form.lead_quality_id} onChange={(v) => set("lead_quality_id", v)} testId="field-lead-quality" /></div>
                </div>
                <div>
                  <FL>Lead Status</FL>
                  <div className="mt-1"><MasterDataSelect listType="lead_status" value={form.lead_status_id} onChange={(v) => set("lead_status_id", v)} placeholder="Defaults to New" testId="field-lead-status" /></div>
                </div>
                <div>
                  <FL>Lead Warmth</FL>
                  <div className="mt-1"><MasterDataSelect listType="lead_warmth" value={form.lead_warmth_id} onChange={(v) => set("lead_warmth_id", v)} testId="field-lead-warmth" /></div>
                </div>
                <div>
                  <FL>Next Contact Date/Time</FL>
                  <Input
                    type="datetime-local"
                    value={form.next_contact_date ? form.next_contact_date.slice(0, 16) : ""}
                    onChange={(e) => set("next_contact_date", e.target.value ? new Date(e.target.value).toISOString() : "")}
                    className="mt-1 font-mono-num"
                    data-testid="field-next-contact-date"
                  />
                </div>
                <div className="md:col-span-2">
                  <FL>Groups</FL>
                  <Select value={form.group_ids[0] || "__none__"} onValueChange={(v) => set("group_ids", v === "__none__" ? [] : [v])}>
                    <SelectTrigger className="mt-1" data-testid="field-groups"><SelectValue placeholder="Pick a group (optional)…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <FL>Tags</FL>
                  <div className="mt-1">
                    <MasterDataMultiSelect
                      listType="tags"
                      values={form.tag_ids}
                      onChange={(v) => set("tag_ids", v)}
                      allowAdd
                      placeholder="Search or add new tag…"
                      testId="field-tags"
                    />
                  </div>
                </div>
                {isAdmin && (
                  <div className="md:col-span-3">
                    <FL>Lead Owner</FL>
                    <Select value={form.lead_owner_id || "__self__"} onValueChange={(v) => set("lead_owner_id", v === "__self__" ? "" : v)}>
                      <SelectTrigger className="mt-1" data-testid="field-lead-owner"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__self__">Me (default)</SelectItem>
                        {usersList.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="md:col-span-3 border-t pt-4 mt-2">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <Checkbox checked={form.lead_closed} onCheckedChange={(v) => set("lead_closed", !!v)} data-testid="field-lead-closed" />
                    Mark lead as closed
                  </label>
                  {form.lead_closed && (
                    <div className="mt-3">
                      <FL required>Closure Reason</FL>
                      <div className="mt-1"><MasterDataSelect listType="closure_reason" value={form.closure_reason_id} onChange={(v) => set("closure_reason_id", v)} testId="field-closure-reason" /></div>
                      {errors.closure_reason_id && <p className="text-xs text-red-700 mt-1">{errors.closure_reason_id}</p>}
                    </div>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <div className="fixed bottom-0 left-64 right-0 bg-white border-t border-slate-200 px-8 py-4 flex items-center justify-end gap-3 z-50 pr-44">
        <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-button">Cancel</Button>
        <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="save-lead-button">
          <Save className="h-4 w-4 mr-1.5" />
          {submitting ? "Saving…" : isEdit ? (client ? "Update Client" : "Update Lead") : (client ? "Save Client" : "Save Lead")}
        </Button>
      </div>
    </form>
  );
}
